import type { FulfillmentMode, ProductStatus } from './client';

/**
 * Why a published product cannot actually be bought.
 *
 * Every sentence here corresponds to a refusal that already exists on the
 * server, and nothing is guessed:
 *
 *  - `checkoutService.quote` counts available stock for the product *and the
 *    chosen variant* and throws `outOfStock` when it is short, so a variant
 *    with an empty pool is unbuyable even when its siblings are full
 *    (`packages/core/src/services/checkout.ts`).
 *  - `assertPayable` in `apps/api/src/routes/checkout.ts` refuses a quote whose
 *    subtotal is zero — there is no zero-payment fulfilment path — so a price of
 *    0 is a wall, not a giveaway.
 *  - the home screen is a grid of categories, so a product filed under none of
 *    them is live and unreachable.
 *
 * The list is deliberately empty for a draft: a product nobody can see yet is
 * not broken, and an alarm on every unfinished row teaches the owner to ignore
 * the ones that matter.
 */
export interface Sellable {
  readonly status: ProductStatus;
  readonly tracksInventory: boolean;
  readonly fulfillmentMode: FulfillmentMode;
  /** Minor units, as a digit string. Null when the amount has not been typed. */
  readonly basePriceMinor: string | null;
  readonly categoryId: string | null;
  /** Null means stock is not counted at all — not "none left". */
  readonly available: number | null;
  readonly variants: ReadonlyArray<{ readonly title: string; readonly available: number | null }>;
}

export function blockingIssues(product: Sellable): string[] {
  if (product.status !== 'published') return [];

  const issues: string[] = [];

  if (product.basePriceMinor !== null && /^0*$/.test(product.basePriceMinor)) {
    issues.push('Цена 0 — оплатить такой заказ нельзя, магазин откажет на расчёте.');
  }

  if (product.tracksInventory) {
    /*
     * Stock is held per variant, so the product-level count is not the answer
     * when there are variants: it adds up every pool, and one full option can
     * hide four empty ones behind a comfortable-looking total.
     */
    if (product.variants.length > 0) {
      const empty = product.variants.filter((variant) => (variant.available ?? 0) === 0);
      if (empty.length === product.variants.length) {
        issues.push('Ни один вариант нельзя купить: склад пуст.');
      } else if (empty.length > 0) {
        issues.push(
          empty.length === 1
            ? `Вариант «${empty[0]!.title}» без остатка — его не купят.`
            : `${empty.length} вариантов без остатка — их не купят.`,
        );
      }
    } else if ((product.available ?? 0) === 0) {
      issues.push('Склад пуст — оплату не примут.');
    }
  }

  if (product.categoryId === null) {
    issues.push('Без категории — с главной покупатель до товара не дойдёт.');
  }

  return issues;
}

/** The short line a list row has room for. */
export const shortIssue = (issues: readonly string[]): string | null =>
  issues.length === 0 ? null : issues.length === 1 ? issues[0]! : `Проблем: ${issues.length}`;
